import { escapeHtml, toast } from './util.js';
import {
  activateEditor, deactivateEditor, applyOverrides, setRebuildHandler, enhanceFromRuntime,
  loadOverridesIntoEditor, getEditorState,
} from './editor.js';
import {
  currentContext, setContext, setCurrentCanvasId, getCurrentCanvasId,
} from './session.js';
import {
  addCanvas, saveCanvasOverrides, nextCanvasName, updateSelectBarSub,
  findCreativeById, findTitleById, findFileById, renameCanvas, deleteCanvas,
} from './library.js';
import { fetchFileSource } from './api.js';

// Re-export so existing importers (select.js) keep working.
export { currentContext } from './session.js';

const previewEls = {
  panel: document.getElementById('previewPanel'),
  name: document.getElementById('previewName'),
  path: document.getElementById('previewPath'),
  iframe: document.getElementById('previewIframe'),
  placeholder: document.getElementById('previewPlaceholder'),
  closeBtn: document.getElementById('previewClose'),
  deviceFrame: document.getElementById('deviceFrame'),
  devicePicker: document.getElementById('devicePicker'),
  devicePickerTrigger: document.getElementById('devicePickerTrigger'),
  devicePickerPanel: document.getElementById('devicePickerPanel'),
  dpName: document.getElementById('dpName'),
  dpDim: document.getElementById('dpDim'),
  reloadBtn: document.getElementById('previewReload'),
  rotateBtn: document.getElementById('previewRotate'),
  canvasTabs: document.getElementById('canvasTabs'),
};

// ===== Device catalog =====
// Each device has its own bezel appearance (cls) and signature details to inject.
const devices = [
  {
    id: 'iphone-15-pro', name: 'iPhone 15 Pro', cls: 'iphone-pro',
    portrait: { w: 360, h: 780 }, scale: { w: 393, h: 852 },
    details: ['dynamic-island'], iconType: 'phone',
  },
  {
    id: 'iphone-se', name: 'iPhone SE', cls: 'iphone-se',
    portrait: { w: 340, h: 600 }, scale: { w: 375, h: 667 },
    details: ['home'], iconType: 'phone',
  },
  {
    id: 'galaxy-s24', name: 'Galaxy S24', cls: 'galaxy',
    portrait: { w: 350, h: 780 }, scale: { w: 360, h: 800 },
    details: ['punch'], iconType: 'phone',
  },
  {
    id: 'ipad-mini', name: 'iPad mini', cls: 'ipad-mini',
    portrait: { w: 460, h: 680 }, scale: { w: 744, h: 1133 },
    details: [], iconType: 'tablet',
  },
  {
    id: 'ipad-pro', name: 'iPad Pro 11', cls: 'ipad-pro',
    portrait: { w: 480, h: 700 }, scale: { w: 834, h: 1194 },
    details: [], iconType: 'tablet',
  },
];

const ICON_PHONE = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="5" y="2" width="14" height="20" rx="2"/><line x1="12" y1="18" x2="12.01" y2="18"/></svg>`;
const ICON_TABLET = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="4" y="2" width="16" height="20" rx="2"/><line x1="12" y1="18" x2="12.01" y2="18"/></svg>`;

let currentBlobUrl = null;
let currentDevice = devices[0];        // default: iPhone 15 Pro
let currentOrientation = 'portrait';   // 'portrait' | 'landscape'
let currentSourceText = '';            // original HTML text for re-injection

// In-memory cache of canvas base HTML (canvasId -> source) so re-opening a
// canvas / switching tabs doesn't re-download multi-MB sources from R2.
const sourceCache = new Map();
const SOURCE_CACHE_MAX = 8;
function cacheSource(id, text) {
  if (!id) return;
  sourceCache.delete(id);
  sourceCache.set(id, text);
  if (sourceCache.size > SOURCE_CACHE_MAX) sourceCache.delete(sourceCache.keys().next().value);
}

// Device frame breathing room inside the stage. Smaller values let the device
// extend into the stage's CSS padding area for a near-edge-to-edge look.
const FRAME_SIDE_MARGIN = 12;
const FRAME_TOP_MARGIN = 12;

export function activatePreview({ fileName, path, file, storedFile, canvas, source, titleId, creativeId, fileId }) {
  previewEls.name.textContent = fileName;
  previewEls.path.textContent = path;
  document.body.classList.add('has-preview');
  setContext(titleId || null, creativeId || null, fileId || (canvas ? canvas.fileId : null));
  setCurrentCanvasId(canvas ? canvas.id : null);

  // ResizeObserver should refit on its own, but the grid-column transition runs
  // 550ms — ensure one final refit after the panel has reached its target width.
  requestAnimationFrame(() => refitDevice());
  setTimeout(() => refitDevice(), 600);

  // Clean up previous blob URL
  if (currentBlobUrl) {
    URL.revokeObjectURL(currentBlobUrl);
    currentBlobUrl = null;
  }

  if (canvas) {
    // Saved canvas — base source is file-owned (소분류): preloaded > cached > R2 fetch.
    const fid = canvas.fileId;
    const cached = sourceCache.get(fid);
    const ready = (source != null) ? Promise.resolve(source)
      : (cached != null) ? Promise.resolve(cached)
      : fetchFileSource(fid);
    ready.then((text) => {
      cacheSource(fid, text);
      currentSourceText = text;
      loadSourceIntoIframe(text);
      activateEditor(text);
      loadOverridesIntoEditor({ pkg: canvas.pkg, tw: canvas.tw });
      renderCanvasBar();
    }).catch((e) => { console.warn('[preview] source load failed:', e.message); toast('원본 로드 실패'); });
  } else if (source != null) {
    // Preloaded raw source (fresh upload shown instantly; canvas persists in bg)
    currentSourceText = source;
    loadSourceIntoIframe(source);
    activateEditor(source);
    renderCanvasBar();
  } else if (file) {
    // Uploaded file — read and use as blob
    file.text().then(text => {
      currentSourceText = text;
      loadSourceIntoIframe(text);
      activateEditor(text);
      renderCanvasBar();
    });
  } else if (storedFile) {
    // Stored file (mock) — generate placeholder HTML
    const mockHtml = generateMockHtml(storedFile, path);
    currentSourceText = mockHtml;
    loadSourceIntoIframe(mockHtml);
    activateEditor(mockHtml);
  }
  renderCanvasBar();
}

// ===== Canvas bar (top of preview) — tabs + Save/+ actions =====
// Tab: click = switch · double-click = rename · hover × = delete (two-step).
// Save = overwrite current canvas · + = create new canvas from current values.
function renderCanvasBar() {
  const el = previewEls.canvasTabs;
  if (!el) return;
  if (!document.body.classList.contains('has-preview')) { el.style.display = 'none'; el.innerHTML = ''; return; }

  const file = currentContext.fileId ? findFileById(currentContext.fileId) : null;
  const canvases = file ? (file.canvases || []) : [];
  const curId = getCurrentCanvasId();
  el.innerHTML = '';

  const list = document.createElement('div');
  list.className = 'canvas-tabs-list';
  canvases.forEach((cv) => list.appendChild(buildCanvasTab(file, cv, cv.id === curId)));
  el.appendChild(list);

  const actions = document.createElement('div');
  actions.className = 'canvas-tabs-actions';

  const saveBtn = document.createElement('button');
  saveBtn.type = 'button';
  saveBtn.id = 'canvasSave';
  saveBtn.className = 'canvas-action canvas-action-save';
  saveBtn.textContent = 'Save';
  saveBtn.title = '현재 캔버스에 저장';
  saveBtn.disabled = curId === null;
  saveBtn.addEventListener('click', () => saveCanvas('current'));
  actions.appendChild(saveBtn);

  const newBtn = document.createElement('button');
  newBtn.type = 'button';
  newBtn.id = 'canvasNew';
  newBtn.className = 'canvas-action canvas-action-new';
  newBtn.textContent = '+';
  newBtn.title = '새 캔버스 생성';
  newBtn.addEventListener('click', () => saveCanvas('new'));
  actions.appendChild(newBtn);

  el.appendChild(actions);
  el.style.display = '';
}

function buildCanvasTab(file, cv, active) {
  const tab = document.createElement('div');
  tab.className = 'canvas-tab' + (active ? ' active' : '');

  const label = document.createElement('button');
  label.type = 'button';
  label.className = 'canvas-tab-label';
  label.textContent = cv.name;
  label.title = `${cv.name} · 더블클릭하여 이름 변경`;
  label.addEventListener('click', () => { if (cv.id !== getCurrentCanvasId()) switchToCanvas(cv); });
  label.addEventListener('dblclick', (e) => { e.preventDefault(); startTabRename(tab, file, cv); });
  tab.appendChild(label);

  const del = document.createElement('button');
  del.type = 'button';
  del.className = 'canvas-tab-del';
  del.textContent = '×';
  del.title = '삭제';
  let armed = false, armTimer = null;
  del.addEventListener('click', (e) => {
    e.stopPropagation();
    if (!armed) {
      armed = true;
      del.classList.add('confirm');
      del.title = '한 번 더 클릭하면 삭제';
      armTimer = setTimeout(() => { armed = false; del.classList.remove('confirm'); }, 2500);
      return;
    }
    if (armTimer) clearTimeout(armTimer);
    removeCanvas(file, cv);
  });
  tab.appendChild(del);

  return tab;
}

function startTabRename(tab, file, cv) {
  const input = document.createElement('input');
  input.type = 'text';
  input.className = 'canvas-tab-input';
  input.value = cv.name;
  const commit = () => {
    const n = input.value.trim();
    if (n && n !== cv.name) renameCanvas(cv, n).catch((e) => { console.warn('[preview] rename failed:', e.message); toast('이름 변경 실패'); });
    finish();
  };
  const finish = () => {
    renderCanvasBar();
    if (getCurrentCanvasId() === cv.id) previewEls.name.textContent = cv.name;
  };
  input.addEventListener('keydown', (e) => {
    if (e.key === 'Enter') { e.preventDefault(); commit(); }
    else if (e.key === 'Escape') { e.preventDefault(); finish(); }
  });
  input.addEventListener('blur', commit);
  tab.innerHTML = '';
  tab.appendChild(input);
  input.focus();
  input.select();
}

function removeCanvas(file, cv) {
  const wasCurrent = getCurrentCanvasId() === cv.id;
  deleteCanvas(file, cv).catch((e) => { console.warn('[preview] delete failed:', e.message); toast('삭제 실패'); });
  updateSelectBarSub();
  const remaining = file.canvases || [];
  if (wasCurrent) {
    if (remaining.length) switchToCanvas(remaining[0]);
    else { setCurrentCanvasId(null); renderCanvasBar(); }
  } else {
    renderCanvasBar();
  }
}

function switchToCanvas(cv) {
  const file = findFileById(cv.fileId);
  const creative = file ? findCreativeById(file.creativeId) : null;
  const title = creative ? findTitleById(creative.titleId) : null;
  activatePreview({
    fileName: file ? file.name : cv.name,
    path: `${title ? title.name : ''} / ${creative ? creative.name : ''} / ${cv.name}`,
    canvas: cv,
    titleId: title ? title.id : null,
    creativeId: creative ? creative.id : null,
    fileId: cv.fileId,
  });
}

// Attach a just-persisted canvas to the live preview (used by the upload flow,
// which shows the preview instantly from the local source, then saves in the
// background). Updates context/header/tabs and caches the file's source.
export function setActivePreviewCanvas(canvas, title, creative, file, source) {
  setContext(title.id, creative.id, file.id);
  setCurrentCanvasId(canvas.id);
  if (source != null) cacheSource(file.id, source);
  previewEls.name.textContent = file.name;
  previewEls.path.textContent = `${title.name} / ${creative.name} / ${canvas.name}`;
  renderCanvasBar();
}

function loadSourceIntoIframe(text) {
  if (currentBlobUrl) URL.revokeObjectURL(currentBlobUrl);
  const blob = new Blob([text], { type: 'text/html' });
  currentBlobUrl = URL.createObjectURL(blob);
  previewEls.iframe.src = currentBlobUrl;
  previewEls.iframe.style.display = 'block';
  previewEls.placeholder.style.display = 'none';

  // Once the playable has booted, query its Bridge.NET class metadata for
  // section/title decorators and feed them to the editor.
  previewEls.iframe.addEventListener('load', pollForRuntimeMetadata, { once: true });
}

function pollForRuntimeMetadata() {
  const win = previewEls.iframe.contentWindow;
  if (!win) return;
  let attempts = 0;
  const tick = () => {
    attempts++;
    let klass;
    try { klass = win.PlayworksConfigVariableManager; } catch { /* cross-origin */ return; }
    if (klass && typeof klass.$getMetadata === 'function') {
      enhanceFromRuntime(win);
      return;
    }
    if (attempts < 40) setTimeout(tick, 250); // up to ~10 s
  };
  tick();
}

// Editor → preview bridge: rebuild blob with overrides injected
setRebuildHandler(({ pkg, tw }) => {
  if (!currentSourceText) return;
  const patched = applyOverrides(currentSourceText, pkg, tw);
  loadSourceIntoIframe(patched);
});

// Save bridge: persist the current dynamic values into a canvas of the open file.
//   'current' → overwrite the open canvas
//   'new'     → create a new canvas (variant) under the same file
let saveInFlight = false;
async function saveCanvas(mode) {
  if (saveInFlight) return;
  saveInFlight = true;
  setSaveButtonsDisabled(true);
  try {
    const file = currentContext.fileId ? findFileById(currentContext.fileId) : null;
    if (!file) { toast('열린 파일이 없습니다'); return; }
    const { pkg, tw } = getEditorState();

    if (mode === 'current') {
      const cv = (file.canvases || []).find(c => c.id === getCurrentCanvasId());
      if (!cv) { toast('열린 캔버스가 없습니다 — + 로 새 캔버스를 만드세요'); return; }
      await saveCanvasOverrides(cv, pkg, tw);
      toast(`저장됨 · ${cv.name}`);
    } else {
      const name = nextCanvasName(file);
      const rec = await addCanvas({ file, pkg, tw, name });
      setCurrentCanvasId(rec.id);
      updateSelectBarSub();
      const creative = findCreativeById(currentContext.creativeId);
      const title = findTitleById(currentContext.titleId);
      previewEls.path.textContent = `${title ? title.name : ''} / ${creative ? creative.name : ''} / ${name}`;
      toast(`새 캔버스 생성 · ${name}`);
    }
  } catch (e) {
    console.warn('[preview] save failed:', e);
    toast('저장에 실패했습니다');
  } finally {
    saveInFlight = false;
    renderCanvasBar();
  }
}

function setSaveButtonsDisabled(disabled) {
  const s = document.getElementById('canvasSave');
  const n = document.getElementById('canvasNew');
  if (s) s.disabled = disabled;
  if (n) n.disabled = disabled;
}

export function deactivatePreview() {
  document.body.classList.remove('has-preview');
  setContext(null, null, null);
  setCurrentCanvasId(null);
  renderCanvasBar();
  currentSourceText = '';
  deactivateEditor();
  // Slight delay so the slide-out animation completes before clearing
  setTimeout(() => {
    if (currentBlobUrl) {
      URL.revokeObjectURL(currentBlobUrl);
      currentBlobUrl = null;
    }
    previewEls.iframe.src = 'about:blank';
    previewEls.iframe.style.display = 'none';
    previewEls.placeholder.style.display = 'grid';
  }, 600);
}

previewEls.closeBtn.addEventListener('click', deactivatePreview);

// ===== Device system =====
function applyDevice(device) {
  const f = previewEls.deviceFrame;
  // Reset all device variant classes
  devices.forEach(d => f.classList.remove(d.cls));
  f.classList.add(device.cls);
  // Orientation class — used by CSS to swap asymmetric paddings (e.g. iPhone SE)
  // and to hide portrait-only details that wouldn't make sense rotated.
  f.classList.toggle('landscape', currentOrientation === 'landscape');

  // Intrinsic dimensions — swap for landscape so the frame box itself is rotated
  const w = currentOrientation === 'landscape' ? device.portrait.h : device.portrait.w;
  const h = currentOrientation === 'landscape' ? device.portrait.w : device.portrait.h;
  f.dataset.baseWidth = w;
  f.dataset.baseHeight = h;

  // Inject signature details
  f.querySelectorAll('.device-detail').forEach(el => el.remove());
  device.details.forEach(d => {
    const el = document.createElement('div');
    el.className = 'device-detail ' + d;
    f.insertBefore(el, f.firstChild);
  });
  // Update picker label (dimensions reflect orientation)
  previewEls.dpName.textContent = device.name;
  const dimW = currentOrientation === 'landscape' ? device.scale.h : device.scale.w;
  const dimH = currentOrientation === 'landscape' ? device.scale.w : device.scale.h;
  previewEls.dpDim.textContent = `${dimW} × ${dimH}`;

  // Fit to current stage size
  refitDevice();
}

// Fit the device frame within the stage (both axes). Width/height are set directly
// (no transform) so the layout box equals the visual box and grid centering works.
// The browser-zoom counter-scale is applied separately on top of this (see below).
function refitDevice() {
  const f = previewEls.deviceFrame;
  const stage = f.parentElement;
  if (!stage) return;
  const stageW = stage.clientWidth;
  const stageH = stage.clientHeight;
  if (stageW === 0 || stageH === 0) return; // not visible yet — observer will retry
  // Remember the device's intrinsic (original) dimensions on the frame so we
  // always scale from the original, not from the previously-scaled value.
  const baseW = parseFloat(f.dataset.baseWidth || '0');
  const baseH = parseFloat(f.dataset.baseHeight || '0');
  if (!baseW || !baseH) return;
  const availW = stageW - FRAME_SIDE_MARGIN * 2;
  const availH = stageH - FRAME_TOP_MARGIN * 2;
  const scaleW = availW > 0 ? availW / baseW : 1;
  const scaleH = availH > 0 ? availH / baseH : 1;
  // Fit within the stage (cap 1.6× so huge monitors don't blow up the bezel).
  const scale = Math.min(1.6, scaleW, scaleH);
  f.style.width = (baseW * scale) + 'px';
  f.style.height = (baseH * scale) + 'px';
}

// The device preview is detached from browser zoom: zooming the page enlarges
// everything else, but the device stays at exactly its 100% on-screen size. We
// counter the zoom with an inverse transform (devicePixelRatio tracks zoom). When
// only the zoom changed we DON'T refit (keep the 100% CSS size) and suppress refit
// for the panel-width transition the zoom triggers; genuine resizes still refit.
// (At extreme zoom the device may be clipped by the stage edge — accepted, since
// keeping it at 100% is the priority. Mobile pinch-zoom doesn't change
// devicePixelRatio, so the device magnifies naturally with the pinch.)
const REF_DPR = window.devicePixelRatio || 1;   // the user's load-time ("100%") zoom
function applyZoomCancel() {
  const z = (window.devicePixelRatio || 1) / REF_DPR;
  previewEls.deviceFrame.style.transformOrigin = 'center center';
  previewEls.deviceFrame.style.transform = Math.abs(z - 1) > 0.001 ? `scale(${1 / z})` : '';
}
let lastDpr = REF_DPR;
let zoomSettleUntil = 0;
const stageResizeObserver = new ResizeObserver(() => {
  const dpr = window.devicePixelRatio || 1;
  const now = Date.now();
  if (Math.abs(dpr - lastDpr) > 0.001) {     // zoom changed → pin to 100%, don't refit
    lastDpr = dpr;
    zoomSettleUntil = now + 650;             // swallow the 0.55s panel transition the zoom triggers
    applyZoomCancel();
    return;
  }
  if (now < zoomSettleUntil) return;
  refitDevice();
});
stageResizeObserver.observe(previewEls.deviceFrame.parentElement);

// Render device picker options
function renderDevicePicker() {
  previewEls.devicePickerPanel.innerHTML = '';
  devices.forEach(d => {
    const opt = document.createElement('div');
    opt.className = 'device-option' + (d.id === currentDevice.id ? ' selected' : '');
    opt.innerHTML = `
      <div class="opt-mini">${d.iconType === 'tablet' ? ICON_TABLET : ICON_PHONE}</div>
      <div class="opt-info">
        <div class="opt-name">${escapeHtml(d.name)}</div>
        <div class="opt-dim">${d.scale.w} × ${d.scale.h}</div>
      </div>
      <div class="opt-check">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
      </div>
    `;
    opt.addEventListener('click', () => {
      previewEls.devicePicker.classList.remove('open');
      if (d.id === currentDevice.id) return;
      currentDevice = d;
      applyDevice(currentDevice);
      renderDevicePicker();
      reloadIframe();
    });
    previewEls.devicePickerPanel.appendChild(opt);
  });
}

function reloadIframe() {
  if (!currentBlobUrl) return;
  const url = currentBlobUrl;
  previewEls.iframe.src = 'about:blank';
  setTimeout(() => { previewEls.iframe.src = url; }, 30);
}

// Device picker open/close
previewEls.devicePickerTrigger.addEventListener('click', (e) => {
  e.stopPropagation();
  previewEls.devicePicker.classList.toggle('open');
});
document.addEventListener('click', (e) => {
  if (!e.target.closest('#devicePicker')) {
    previewEls.devicePicker.classList.remove('open');
  }
});

// Initialize device on load
applyDevice(currentDevice);
renderDevicePicker();

// Reload button
previewEls.reloadBtn.addEventListener('click', reloadIframe);

// Rotate button — toggle portrait/landscape and re-apply current device
previewEls.rotateBtn.addEventListener('click', () => {
  currentOrientation = currentOrientation === 'portrait' ? 'landscape' : 'portrait';
  applyDevice(currentDevice);
  renderDevicePicker();
  reloadIframe();
});

function generateMockHtml(f, pathLabel) {
  const safeName = escapeHtml(f.name);
  const safePath = escapeHtml(pathLabel);
  return `<!DOCTYPE html><html><head><meta charset="UTF-8"><title>${safeName}</title>
<style>
  body{margin:0;display:grid;place-items:center;height:100vh;background:#1a1a17;color:#f5f4ef;font-family:system-ui;text-align:center;padding:20px}
  h1{margin:0 0 8px;font-weight:500;font-size:18px;letter-spacing:-0.02em}
  .meta{opacity:0.5;font-size:11px;font-family:ui-monospace,monospace;margin-top:4px}
  .badge{display:inline-block;padding:3px 10px;border-radius:999px;font-size:10px;font-family:ui-monospace,monospace;background:#3a2014;color:#f0997b;margin-top:12px}
</style></head><body><div>
<h1>${safeName}</h1>
<div class="meta">${safePath}</div>
<div class="badge">${escapeHtml(f.network)} · asset ${f.assetId}</div>
</div></body></html>`;
}
